import { type FormEvent, useState, useEffect } from 'react';
import {
  Backdrop,
  Button,
  Checkbox,
  Fade,
  IconButton,
  ListItemText,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  type SelectChangeEvent,
  Typography,
  DialogTitle,
} from '@mui/material';
import FileSaver from 'file-saver';
import moment from 'moment';
import CLoseIcon from '@mui/icons-material/Close';
import { courseDetailModal } from './styles';
import { type Company, type Course } from '../../utils/type';
import { exportResults, getCompany } from '../../api/apis';
import { CURRENT_USER_COMPANY, CURRENT_USER_ROLE } from '../../hooks/useLogin';
import { TokenExpiredError } from '../../api/error';
import { useHistory } from 'react-router-dom';
import { ROLES, ROUTES } from '../../utils/constant';

import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      backgroundColor: 'rgb(229, 240, 249)',
    },
  },
};

interface SelectData {
  courses: Array<{
    courseid: string;
    courseName: string;
  }>;
  quiz: Array<{
    quizid: string;
    quizName: string;
  }>;
  departments: Array<{
    depName: string;
  }>;
}

interface FormData {
  courses: string[];
  quiz: string[];
  departments: string[];
}

const initFormValues: FormData = {
  courses: ['ALL'],
  quiz: ['ALL'],
  departments: ['ALL'],
};

export default function CourseReportDetailForm ({ courses }: { courses: Course[]; }) {
  const { t } = useTranslation();
  const history = useHistory();
  const userRole = sessionStorage.getItem(CURRENT_USER_ROLE);
  const currentCompany = sessionStorage.getItem(CURRENT_USER_COMPANY);

  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [formData, setFormData] = useState<FormData>(initFormValues);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  // state to store Dropdown form values after API response
  const [selectData, setSelectData] = useState<SelectData>({
    courses: [
      {
        courseName: 'ALL',
        courseid: 'ALL',
      },
    ],
    quiz: [
      {
        quizid: 'ALL',
        quizName: 'ALL',
      },
    ],
    departments: [
      {
        depName: 'ALL',
      },
    ],
  });

  useEffect(() => {
    if (userRole === ROLES.platformAdmin) {
      getCompany().then(res => {
        setCompanies(res.data.company);
        setSelectedCompanies(res.data.company);
      });
    } else if (userRole === ROLES.departmentAdmin) {
      getCompany(currentCompany).then(res => {
        setCompanies([res.data.company]);
        setSelectedCompanies([res.data.company]);
      });
    }
  }, []);

  const handleOpen = async () => {
    if (!courses) return;
    const quizT: Array<{ quizName: string; quizid: string; }> = courses.filter(c => c.courseid.endsWith('Q')).map(c => ({ quizName: c.category1, quizid: c.courseid }));
    const courseT: Array<{ courseName: string; courseid: string; }> = courses.filter(c => !c.courseid.endsWith('Q')).map(c => ({ courseName: c.category1, courseid: c.courseid }));
    const disDep: Array<{ depName: string; }> = [];
    companies.forEach(company => {
      company.departments.forEach(department => {
        disDep.push({ depName: department });
      });
    });

    setSelectData({
      courses: [
        {
          courseName: 'ALL',
          courseid: 'ALL',
        }, ...courseT,
      ],
      quiz: [
        {
          quizid: 'ALL',
          quizName: 'ALL',
        }, ...quizT,
      ],
      departments: [
        {
          depName: 'ALL',
        }, ...disDep,
      ],
    });

    setFormData({
      courses: ['ALL', ...courseT.map(item => item.courseid)],
      quiz: ['ALL', ...quizT.map(item => item.quizid)],
      departments: ['ALL', ...disDep.map(item => item.depName)],
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    setFormData(initFormValues);
    setIsOpen(false);
    setSubmitError('');
    setSubmitting(false);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value, name },
    } = event;
    let difference;

    if (Array.isArray(value)) difference = formData[name].filter(x => !value.includes(x));
    if (difference.indexOf('ALL') > -1 && Array.isArray(difference)) {
      if (name === 'quiz' && formData.courses.length === 0) setSubmitting(true);
      if (name === 'courses') setSubmitting(true);
      if (name === 'departments') setSubmitting(true);
      setFormData(curr => ({
        ...curr,
        [name]: [],
      }));
    } else if (value.includes('ALL')) {
      if (name === 'courses') {
        if (formData.departments.length !== 0) setSubmitting(false);
        setFormData(curr => ({
          ...curr,
          [name]: selectData[name].map(e => e.courseid),
        }));
      }
      if (name === 'quiz') {
        if (formData.departments.length !== 0) setSubmitting(false);
        setFormData(curr => ({
          ...curr,
          [name]: selectData[name].map(e => e.quizid),
        }));
      }
      if (name === 'departments') {
        if (formData.quiz.length !== 0 || formData.courses.length !== 0) setSubmitting(false);
        setFormData(curr => ({
          ...curr,
          [name]: selectData[name].map(e => e.depName),
        }));
      }
    } else {
      setFormData(curr => ({
        ...curr,
        [name]: typeof value === 'string' ? value.split(',') : value,
      }));
      if (!value.length) setSubmitting(true);
      else setSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (Object.values(formData).every(el => el.length === 0)) return;
    setSubmitting(true);
    try {
      let tmpQueryStr = 'courses=';
      if (formData.courses.length !== 0) {
        tmpQueryStr += formData.courses.filter(e => e !== 'ALL').join('!');
      }
      if (formData.quiz.length !== 0) {
        tmpQueryStr += '!' + formData.quiz.filter(e => e !== 'ALL').join('!');
      }
      tmpQueryStr += '&departments=' + formData.departments.filter(e => e !== 'ALL').join('!');
      await exportResults(tmpQueryStr).then(response => {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const date = moment(new Date()).format('YYYYMMDD');
        FileSaver.saveAs(blob, `export_${date}.csv`);
      })
        .catch(err => {
          if (err instanceof TokenExpiredError) {
            history.push(ROUTES.login);
          } else {
            console.error('error in get courses report', err);
            const errMessage = err.message as string;
            setSubmitError(errMessage);
          }
        });
    } catch (err: any) {
      console.error('error in get courses report', err);
      const errMessage = err.message as string;
      setSubmitError(errMessage);
    }
    setSubmitting(false);
  };
  return (
    <div>
      <Button variant="contained" onClick={handleOpen}>
        {t('Export')}
      </Button>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={isOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isOpen}>
          <Paper sx={courseDetailModal} elevation={5}>
            {/* Header */}
            <DialogTitle>
              {t('Export Report')}
              <IconButton className="bi-p" aria-label="close" color="primary" onClick={handleClose}>
                <CLoseIcon />
              </IconButton>
            </DialogTitle>

            {/* FORM */}

            <form onSubmit={handleSubmit}>
              {/* Courses Select Input */}

              <div className="form-element">
                <label htmlFor="course">{t('Course')}:</label>
                <Select
                  id="courses"
                  name="courses"
                  multiple
                  value={formData.courses}
                  onChange={handleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const render = [];
                    if (selected.find(e => e === 'ALL')) {
                      return ['ALL'];
                    } else {
                      selected.forEach((e) => {
                        const index = selectData.courses.findIndex(data => data.courseid === e);
                        render.push(selectData.courses[index].courseName);
                      });
                      return render.join(', ');
                    }
                  }}
                  MenuProps={MenuProps}
                >
                  {selectData.courses.map((data, i) => (
                    <MenuItem key={i} value={data.courseid}>
                      <ListItemText primary={data.courseName} />
                      <Checkbox checked={formData.courses.includes(data.courseid)} />
                    </MenuItem>
                  ))}

                  {selectData.courses.length === 0 && <span>loading...</span>}
                </Select>
              </div>

              {/* Quiz Select Input */}

              <div className="form-element">
                <label htmlFor="course">{t('Quiz')}:</label>
                <Select
                  id="quiz"
                  name="quiz"
                  multiple
                  value={formData.quiz}
                  onChange={handleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const render = [];
                    if (selected.find(e => e === 'ALL')) {
                      return ['ALL'];
                    } else {
                      selected.forEach((e) => {
                        const index = selectData.quiz.findIndex(data => data.quizid === e);
                        render.push(selectData.quiz[index].quizName);
                      });
                      return render.join(', ');
                    }
                  }}
                  MenuProps={MenuProps}
                >
                  {selectData.quiz.map((data, i) => (
                    <MenuItem key={i} value={data.quizid}>
                      <ListItemText primary={data.quizName} />
                      <Checkbox checked={formData.quiz.includes(data.quizid)} />
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Department Select Input */}

              <div className="form-element">
                <label htmlFor="course">{t('Company')}:</label>
                <Select
                  id="companies"
                  name="companies"
                  multiple
                  value={selectedCompanies.map(company => company.company_name)}
                  onChange={(e) => {
                    const selectedCompanyNames = e.target.value as string[];
                    const selectedCompanies = companies.filter(company => selectedCompanyNames.includes(company.company_name));
                    const availableDepartments: Array<{ depName: string; }> = [{ depName: 'ALL' }];
                    selectedCompanies.forEach(company => {
                      company.departments.forEach(department => {
                        availableDepartments.push({ depName: department });
                      });
                    });
                    setSelectData((prev) => {
                      return {
                        ...prev,
                        departments: availableDepartments,
                      };
                    });
                    setFormData((prev) => {
                      return {
                        ...prev,
                        departments: ['ALL', ...availableDepartments.map(dep => dep.depName)],
                      };
                    });
                    setSelectedCompanies(selectedCompanies);
                  }}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const render = [];
                    if (selected.find(e => e === 'ALL')) {
                      return ['ALL'];
                    } else {
                      selected.forEach((e) => {
                        const index = companies.findIndex(data => data.company_name === e);
                        render.push(companies[index].company_name);
                      });
                      return render.join(', ');
                    }
                  }}
                  MenuProps={MenuProps}
                >
                  {companies.map((data, i) => (
                    <MenuItem key={i} value={data.company_name}>
                      <ListItemText primary={data.company_name} />
                      <Checkbox checked={selectedCompanies.includes(data)} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="form-element">
                <label htmlFor="course">{t('Department')}:</label>
                <Select
                  id="departments"
                  name="departments"
                  multiple
                  value={formData.departments}
                  onChange={handleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const render = [];
                    if (selected.find(e => e === 'ALL')) {
                      return ['ALL'];
                    } else {
                      selected.forEach((e) => {
                        const index = selectData.departments.findIndex(data => data.depName === e);
                        render.push(selectData.departments[index].depName);
                      });
                      return render.join(', ');
                    }
                  }}
                  MenuProps={MenuProps}
                >
                  {selectData.departments.map((data, i) => (
                    <MenuItem key={i} value={data.depName}>
                      <ListItemText primary={data.depName} />
                      <Checkbox checked={formData.departments.includes(data.depName)} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="btn-container">
                <Button variant="contained" type="submit" disabled={submitting}>
                  {t('Export')}
                </Button>
              </div>
              {submitError && (
                <Typography variant="subtitle1" color="red">
                  error: {submitError}
                </Typography>
              )}
            </form>
          </Paper>
        </Fade>
      </Modal>
    </div>
  );
}
